/**
 * The Engine Room's view of one computer, and the only place it looks at the
 * machine-limit contract.
 *
 * Every other part of the Engine Room reads `EngineRoomMachine`. Nothing else
 * in this page touches `NodeCapacityRow`, `NodePressureRow` or the owner's
 * limits. The verdict, the seats, the profile, the sustained guards and the
 * reason a machine is held back all come from #483's `MachineCapacity` - the
 * one model OperationsRoom builds per render and hands to the bottom Machines
 * cards, the Machine Capacity drawer, Resource Health and the Core Observer.
 * This file reads that model; it does not re-derive any of it, so the Engine
 * Room cannot disagree with those surfaces about one machine. The page, the
 * captain status, the work section and the Machinist keep reading this shape.
 *
 * `capacity` carries the whole #483 object through untouched, so the
 * per-machine drill-down (#492) reads the same contract rather than asking the
 * rows again.
 *
 * Every figure here comes from a row. A reading a computer did not take is
 * absent, and absent stays absent - nothing is averaged, defaulted or guessed.
 * No I/O and no clock: the moment arrives as `nowMs`.
 */

import {
  buildMachineCapacity, capacityProfileLabel, ceilingText, machineCapacityFor,
  type CapacityProfileKey, type DispatchStance, type MachineCapacity, type MachineGuardState,
  type MachineMeter,
} from '@brunel/shared';
import type { HeldBackBy, NodeCapacityLike, NodePressureLike, Readiness } from './capacity';
import { connectionState, lastSeenLabel, type ConnectionState, type OperationalStatus } from './presence';
import type {
  AiLocalRuntimeStatusRow, FinishedRun, ModelOfferingRef, NodeOffering, NodePresence, NodeVersion,
  RunningTask,
} from './data';

/** The owner's capacity level for a machine, as #483 derives it from the machine's own numbers. */
export type MachineProfile = CapacityProfileKey;

/**
 * One kind of room a machine has left before it meets one of the owner's limits.
 *
 * `points` is the distance between the reading and the limit - percentage
 * points, or Worker seats for `seats` - positive when there is room and
 * negative when the machine is past it. It is null when either side is Unknown
 * or when there is no limit on that dimension at all.
 */
export interface MachineRemaining {
  readonly key: 'seats' | 'memory' | 'video_memory' | 'cpu' | 'graphics';
  readonly label: string;
  readonly text: string;
  readonly points: number | null;
  /** False when the reading or the limit behind it is Unknown. */
  readonly known: boolean;
  /** True when this dimension has no room left, or is the one holding the machine back. */
  readonly exhausted: boolean;
  readonly detail: string;
}

/** What the local AI runtime on a machine is doing, as the Ollama meters report it. */
export interface MachineLocalRuntime {
  readonly serviceState: 'reachable' | 'unreachable' | 'unknown';
  readonly runtimeVersion: string | null;
  readonly installedModelCount: number | null;
  readonly loadedModels: readonly string[];
  readonly loadedModelsKnown: boolean;
  readonly offeredModels: readonly string[];
}

/** One computer, as the Engine Room reads it. */
export interface EngineRoomMachine {
  readonly nodeId: string;
  readonly name: string;
  readonly connection: ConnectionState;
  readonly lastSeenAt: string | null;
  readonly lastSeen: string;
  readonly runningVersion: string | null;
  readonly targetVersion: string | null;
  /** True when the node is behind the release the owner published; null when unknown. */
  readonly versionBehind: boolean | null;
  readonly seats: number | null;
  readonly activeRuns: number | null;
  readonly placesFree: number | null;
  readonly cpuCount: number | null;
  readonly cpuLoadPercent: number | null;
  readonly memoryFreePercent: number | null;
  readonly memoryTotalBytes: number | null;
  readonly graphicsCount: number | null;
  readonly graphicsUtilPercent: number | null;
  readonly videoMemoryFreePercent: number | null;
  readonly reserveMemoryPercent: number | null;
  readonly reserveGraphicsPercent: number | null;
  readonly memoryHeadroomPercent: number | null;
  readonly graphicsHeadroomPercent: number | null;
  readonly readiness: Readiness;
  readonly readinessPhrase: string;
  readonly readinessLine: string;
  /**
   * The same verdict read in the light of whether the machine is still talking
   * to Brunel.
   *
   * The capacity contract answers "what did the last reading say", and a
   * computer that went quiet an hour ago can still have a last reading that
   * said Ready. Beside a dot that says Offline, the word "ready" is a lie by
   * juxtaposition, so the page shows these instead and keeps the capacity
   * contract's own answer untouched underneath.
   */
  readonly statusPhrase: string;
  readonly statusLine: string;
  readonly heldBackBy: HeldBackBy | null;
  /** Exactly why this machine is not taking work, in the owner's words. */
  readonly reason: string | null;
  readonly accepting: boolean | null;
  /** #483's dispatch stance and its own word for it: "Yes", "Held back", "At its seat limit", "Unknown". */
  readonly stance: DispatchStance;
  readonly stanceLabel: string;
  /**
   * The level the machine's own numbers match, or null when the row does not
   * carry all four limits - an unstated limit is not Balanced and not Custom.
   */
  readonly profile: MachineProfile | null;
  readonly profileLabel: string;
  /** The exact numbers behind the level, e.g. "memory reserve 15% · … · graphics ceiling off". */
  readonly profileLine: string | null;
  /** The two sustained guards, exactly as the engine last evaluated them. */
  readonly guards: readonly MachineGuardState[];
  /** The #483 meters - Workers, CPU, RAM, GPU, VRAM - each with the owner's limit on it. */
  readonly meters: readonly MachineMeter[];
  /** How much room is left before each of the owner's limits, where that is derivable. */
  readonly remaining: readonly MachineRemaining[];
  /** Advisory only: a real reading within a few points of a real limit. */
  readonly nearingLine: string | null;
  readonly observedAt: string | null;
  readonly readingAgeMs: number | null;
  readonly readingAge: string | null;
  readonly stale: boolean;
  readonly localRuntime: MachineLocalRuntime | null;
  /** Runs this computer is holding right now, by their titles. */
  readonly runningTitles: readonly string[];
  /** Finished runs this computer's Workers reported recently, and how many failed. */
  readonly recentRuns: number;
  readonly recentFailures: number;
  /** Runs #483 counts as finished on this machine inside its recent window; null when unreadable. */
  readonly recentWindowRuns: number | null;
  readonly recentWindowMinutes: number;
  /** The #483 model for this computer, carried through whole for the machine drill-down (#492). */
  readonly capacity: MachineCapacity;
}

export interface EngineRoomMachineInput {
  readonly nodes: readonly NodePresence[];
  readonly capacity: readonly NodeCapacityLike[];
  readonly pressure: readonly (NodePressureLike & Partial<{
    readonly cpu_count: number | null;
    readonly memory_total_bytes: number | null;
    readonly memory_free_bytes: number | null;
    readonly agent_memory_bytes: number | null;
    readonly graphics_count: number | null;
    readonly graphics_util_percent: number | null;
    readonly video_memory_total_bytes: number | null;
    readonly video_memory_used_bytes: number | null;
  }>)[];
  readonly versions: readonly NodeVersion[];
  readonly localRuntimes: readonly AiLocalRuntimeStatusRow[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
  readonly running: readonly RunningTask[];
  readonly results: readonly FinishedRun[];
  /**
   * The #483 machine-capacity model, built once for the whole screen. The
   * room passes the one it already built; a caller that has none gets it built
   * here from the same rows, by the same #483 function - never re-derived.
   */
  readonly machineCapacity?: readonly MachineCapacity[];
  readonly nowMs: number;
}

function firstFor<T extends { readonly node_id: string }>(rows: readonly T[], nodeId: string): T | null {
  return rows.find((row) => row.node_id === nodeId) ?? null;
}

/**
 * How many finished runs a machine's Workers reported recently, and how many of
 * those failed. A finished run does not name its computer, so this counts the
 * runs whose still-running siblings ran here only when the run rows say so;
 * where they do not, both figures stay at zero rather than being apportioned.
 */
function recentWork(results: readonly FinishedRun[], running: readonly RunningTask[], nodeName: string): {
  readonly runs: number; readonly failures: number;
} {
  const workerNumbers = new Set(
    running.filter((row) => row.node_name === nodeName && row.worker_number !== null)
      .map((row) => row.worker_number as number),
  );
  if (workerNumbers.size === 0) return { runs: 0, failures: 0 };
  const mine = results.filter((row) => row.worker_number !== null && workerNumbers.has(row.worker_number));
  return { runs: mine.length, failures: mine.filter((row) => !row.success).length };
}

function localRuntimeFor(
  input: EngineRoomMachineInput,
  nodeId: string,
): MachineLocalRuntime | null {
  const row = input.localRuntimes.find((item) => item.node_id === nodeId) ?? null;
  const offeredModels = [...new Set(
    input.nodeOfferings
      .filter((item) => item.node_id === nodeId)
      .map((item) => input.modelOfferings.find((offering) => offering.key === item.offering_key) ?? null)
      .filter((offering): offering is ModelOfferingRef => offering !== null && offering.execution_location === 'local')
      .map((offering) => offering.model_key),
  )].sort();
  if (row === null && offeredModels.length === 0) return null;
  return {
    serviceState: row?.service_state ?? 'unknown',
    runtimeVersion: row?.runtime_version ?? null,
    installedModelCount: row?.installed_models_known === true ? row.installed_models.length : null,
    loadedModels: row?.loaded_models_known === true ? row.loaded_models.map((model) => model.name) : [],
    loadedModelsKnown: row?.loaded_models_known ?? false,
    offeredModels,
  };
}

/**
 * The #483 model for callers that did not bring one - built by #483's own
 * function from the rows this adapter was handed, so there is still only one
 * derivation. A column a row does not carry is passed as absent.
 */
function capacityFromRows(input: EngineRoomMachineInput): readonly MachineCapacity[] {
  const nodeIdByName = new Map(input.nodes.map((node) => [node.name, node.node_id]));
  return buildMachineCapacity({
    nowMs: input.nowMs,
    nodes: input.nodes.map((node) => ({
      node_id: node.node_id, name: node.name, status: node.status, last_seen_at: node.last_seen_at,
    })),
    capacity: input.capacity.map((row) => ({
      node_id: row.node_id,
      name: row.name,
      worker_seats: row.worker_seats,
      reserve_memory_percent: row.reserve_memory_percent,
      reserve_graphics_percent: row.reserve_graphics_percent,
      cpu_ceiling_percent: row.cpu_ceiling_percent ?? null,
      graphics_ceiling_percent: row.graphics_ceiling_percent ?? null,
      observed_at: row.observed_at,
      memory_free_percent: row.memory_free_percent,
      video_memory_free_percent: row.video_memory_free_percent,
      active_runs: row.active_runs,
      cpu_sustained_percent: row.cpu_sustained_percent ?? null,
      cpu_sample_count: (row as { cpu_sample_count?: number | null }).cpu_sample_count ?? null,
      cpu_guard_engaged: (row as { cpu_guard_engaged?: boolean | null }).cpu_guard_engaged ?? null,
      cpu_guard_changed_at: (row as { cpu_guard_changed_at?: string | null }).cpu_guard_changed_at ?? null,
      graphics_sustained_percent: row.graphics_sustained_percent ?? null,
      graphics_sample_count: (row as { graphics_sample_count?: number | null }).graphics_sample_count ?? null,
      graphics_guard_engaged: (row as { graphics_guard_engaged?: boolean | null }).graphics_guard_engaged ?? null,
      graphics_guard_changed_at: (row as { graphics_guard_changed_at?: string | null }).graphics_guard_changed_at ?? null,
      accepting: row.accepting,
      holding_back: row.holding_back,
    })),
    pressure: input.pressure.map((row) => ({
      node_id: row.node_id,
      observed_at: row.observed_at,
      cpu_count: row.cpu_count ?? null,
      cpu_load_percent: row.cpu_load_percent,
      memory_total_bytes: row.memory_total_bytes ?? null,
      memory_free_bytes: row.memory_free_bytes ?? null,
      agent_memory_bytes: row.agent_memory_bytes ?? null,
      memory_free_percent: row.memory_free_percent,
      graphics_count: row.graphics_count ?? null,
      graphics_util_percent: row.graphics_util_percent ?? null,
      video_memory_total_bytes: row.video_memory_total_bytes ?? null,
      video_memory_used_bytes: row.video_memory_used_bytes ?? null,
      video_memory_free_percent: row.video_memory_free_percent,
      active_runs: row.active_runs,
    })),
    versions: input.versions,
    running: input.running.map((row) => ({
      node_id: nodeIdByName.get(row.node_name) ?? null,
      worker_id: null,
      title: row.title,
      started_at: row.started_at,
    })),
    // A finished run does not name its computer in these rows, so the recent
    // count is Unknown here rather than a guessed zero.
    available: { finished: false },
  });
}

const READINESS_BY_STANCE: Readonly<Record<DispatchStance, Readiness>> = {
  accepting: 'ready',
  at_seat_limit: 'at_limit',
  held_back: 'held_back',
  unknown: 'not_reported',
};

const READINESS_PHRASES: Readonly<Record<Readiness, string>> = {
  ready: 'ready',
  at_limit: 'at its limit',
  held_back: 'held back',
  not_reported: 'not reported',
};

function finite(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** "11.5" rather than "11.499999", and never "-0". */
function points(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${Object.is(rounded, -0) ? 0 : rounded}`;
}

/**
 * Whether the row states every limit a level is made of. #483 resolves a
 * missing reserve as zero, which would read as Custom; here a limit the row
 * does not state leaves the level Unknown instead.
 */
function limitsStated(row: NodeCapacityLike | null): boolean {
  return row !== null
    && finite(row.reserve_memory_percent) !== null
    && finite(row.reserve_graphics_percent) !== null
    && row.cpu_ceiling_percent !== undefined
    && row.graphics_ceiling_percent !== undefined;
}

function reserveRemaining(
  key: 'memory' | 'video_memory',
  label: string,
  free: number | null,
  reserve: number | null,
  holding: boolean,
): MachineRemaining {
  if (free === null || reserve === null) {
    return {
      key, label, text: 'Unknown', points: null, known: false, exhausted: false,
      detail: free === null
        ? `This machine has not reported its ${label.toLowerCase()}, so the room left above your reserve is Unknown.`
        : `The share of ${label.toLowerCase()} you keep free is not stated, so the room left is Unknown.`,
    };
  }
  const room = free - reserve;
  return {
    key,
    label,
    text: room >= 0 ? `${points(room)} pts above ${reserve}%` : `${points(-room)} pts under ${reserve}%`,
    points: room,
    known: true,
    exhausted: holding || room < 0,
    detail: `${points(free)}% free against the ${reserve}% you keep for yourself.`,
  };
}

function guardRemaining(guard: MachineGuardState, holding: boolean): MachineRemaining {
  const key = guard.dimension;
  const label = guard.dimension === 'cpu' ? 'CPU' : 'GPU';
  if (guard.ceilingPercent === null) {
    return { key, label, text: 'No ceiling', points: null, known: true, exhausted: false, detail: guard.line };
  }
  if (guard.unmeasured || guard.sustainedPercent === null) {
    return {
      key, label, text: `Unknown vs ${ceilingText(guard.ceilingPercent)}`, points: null, known: false,
      exhausted: false, detail: guard.line,
    };
  }
  const room = guard.ceilingPercent - guard.sustainedPercent;
  return {
    key,
    label,
    text: guard.engaged
      ? `Held: ${points(guard.sustainedPercent)}% vs ${guard.ceilingPercent}%`
      : `${points(room)} pts under ${guard.ceilingPercent}%`,
    points: room,
    known: true,
    exhausted: holding || guard.engaged,
    detail: guard.line,
  };
}

/**
 * How much room each of the owner's limits still leaves on this machine.
 * Seats, then the two reserves, then the two sustained ceilings. A dimension
 * with an unknown reading or an unstated limit says Unknown; a ceiling that is
 * off says so rather than showing a number.
 */
export function machineRemaining(
  capacity: MachineCapacity,
  memoryFree: number | null,
  videoFree: number | null,
  reserveMemory: number | null,
  reserveGraphics: number | null,
): readonly MachineRemaining[] {
  const seats: MachineRemaining = capacity.seatsFree === null || capacity.seatsConfigured === null
    ? {
      key: 'seats', label: 'Workers', text: 'Unknown', points: null, known: false, exhausted: false,
      detail: 'This machine has not stated how many Worker seats it has, or how many are in use.',
    }
    : {
      key: 'seats',
      label: 'Workers',
      text: `${capacity.seatsFree} of ${capacity.seatsConfigured} free`,
      points: capacity.seatsFree,
      known: true,
      exhausted: capacity.seatsFree === 0,
      detail: `${capacity.seatsText} Worker seats in use.`,
    };
  const [cpu, graphics] = capacity.guards;
  return [
    seats,
    reserveRemaining('memory', 'RAM', memoryFree, reserveMemory, capacity.holdBack === 'memory'),
    reserveRemaining('video_memory', 'VRAM', videoFree, reserveGraphics, capacity.holdBack === 'video_memory'),
    ...(cpu === undefined ? [] : [guardRemaining(cpu, capacity.holdBack === 'cpu')]),
    ...(graphics === undefined ? [] : [guardRemaining(graphics, capacity.holdBack === 'graphics')]),
  ];
}

function profileLineOf(capacity: MachineCapacity): string {
  const limits = capacity.limits;
  return [
    `memory reserve ${limits.reserveMemoryPercent}%`,
    `video memory reserve ${limits.reserveGraphicsPercent}%`,
    `processor ceiling ${ceilingText(limits.cpuCeilingPercent)}`,
    `graphics ceiling ${ceilingText(limits.graphicsCeilingPercent)}`,
  ].join(' · ');
}

/**
 * One machine's Engine Room row. The verdict, its line and its reason are the
 * #483 capacity model's, unchanged - this adds only the connection reading and
 * the gauges' raw figures beside them.
 */
export function engineRoomMachine(
  input: EngineRoomMachineInput,
  node: NodePresence,
  model: MachineCapacity | null = null,
): EngineRoomMachine {
  const row = firstFor(input.capacity, node.node_id);
  const pressure = firstFor(input.pressure, node.node_id);
  const version = firstFor(input.versions, node.node_id);
  const capacity = model
    ?? machineCapacityFor(input.machineCapacity ?? capacityFromRows(input), node.node_id)
    ?? buildMachineCapacity({
      nowMs: input.nowMs,
      nodes: [{ node_id: node.node_id, name: node.name, status: node.status, last_seen_at: node.last_seen_at }],
    })[0]!;
  const running = input.running.filter((item) => item.node_name === node.name);
  const recent = recentWork(input.results, input.running, node.name);
  const connection = connectionState({
    lastSeenAt: node.last_seen_at, status: node.status as OperationalStatus, nowMs: input.nowMs,
  });
  const lastSeen = lastSeenLabel(node.last_seen_at, input.nowMs);

  const readiness = READINESS_BY_STANCE[capacity.stance];
  const readinessPhrase = READINESS_PHRASES[readiness];
  const readinessLine = capacity.dispatchLine;
  const statusPhrase = connection === 'disconnected'
    ? 'out of touch'
    : connection === 'stale' ? 'heartbeat late' : readinessPhrase;
  const statusLine = connection === 'disconnected'
    ? `Out of touch: last seen ${lastSeen}. Nothing goes here until it reports again. Its last reading said: ${readinessLine}`
    : connection === 'stale'
      ? `Losing touch: last seen ${lastSeen}. Its newest reading said: ${readinessLine}`
      : readinessLine;

  const memoryFree = finite(row?.memory_free_percent ?? pressure?.memory_free_percent ?? null);
  const videoFree = finite(row?.video_memory_free_percent ?? pressure?.video_memory_free_percent ?? null);
  const reserveMemory = finite(row?.reserve_memory_percent ?? null);
  const reserveGraphics = finite(row?.reserve_graphics_percent ?? null);
  const profile = limitsStated(row) ? capacity.profile : null;

  return {
    nodeId: node.node_id,
    name: node.name,
    connection,
    lastSeenAt: node.last_seen_at,
    lastSeen,
    runningVersion: capacity.agentVersion,
    targetVersion: capacity.targetVersion,
    versionBehind: version === null ? null : version.behind,
    seats: capacity.seatsConfigured,
    activeRuns: capacity.seatsActive,
    placesFree: capacity.seatsFree,
    cpuCount: capacity.cpuCount,
    cpuLoadPercent: finite(pressure?.cpu_load_percent ?? null),
    memoryFreePercent: memoryFree,
    memoryTotalBytes: finite(pressure?.memory_total_bytes ?? null),
    graphicsCount: finite(pressure?.graphics_count ?? null),
    graphicsUtilPercent: finite(pressure?.graphics_util_percent ?? null),
    videoMemoryFreePercent: videoFree,
    reserveMemoryPercent: reserveMemory,
    reserveGraphicsPercent: reserveGraphics,
    memoryHeadroomPercent: memoryFree === null || reserveMemory === null ? null : memoryFree - reserveMemory,
    graphicsHeadroomPercent: videoFree === null || reserveGraphics === null ? null : videoFree - reserveGraphics,
    readiness,
    readinessPhrase,
    readinessLine,
    statusPhrase,
    statusLine,
    heldBackBy: capacity.stance === 'held_back' ? capacity.holdBack : null,
    reason: capacity.stance === 'held_back' ? capacity.holdBackReason : null,
    accepting: capacity.stance === 'unknown' ? null : capacity.stance === 'accepting',
    stance: capacity.stance,
    stanceLabel: capacity.stanceLabel,
    profile,
    profileLabel: profile === null ? 'Unknown' : capacityProfileLabel(profile),
    profileLine: profile === null ? null : profileLineOf(capacity),
    guards: capacity.guards,
    meters: capacity.meters,
    remaining: machineRemaining(capacity, memoryFree, videoFree, reserveMemory, reserveGraphics),
    nearingLine: capacity.nearingLine,
    observedAt: capacity.observedAt,
    readingAgeMs: capacity.readingAgeMs,
    readingAge: capacity.readingAgeLabel,
    stale: capacity.stale,
    localRuntime: localRuntimeFor(input, node.node_id),
    runningTitles: running.map((item) => item.title),
    recentRuns: recent.runs,
    recentFailures: recent.failures,
    recentWindowRuns: capacity.recentRuns,
    recentWindowMinutes: capacity.recentWindowMinutes,
    capacity,
  };
}

/** Every computer the owner has, in the order the Engine Room shows them. */
export function engineRoomMachines(input: EngineRoomMachineInput): readonly EngineRoomMachine[] {
  const models = input.machineCapacity ?? capacityFromRows(input);
  return input.nodes
    .map((node) => engineRoomMachine(input, node, machineCapacityFor(models, node.node_id)))
    .sort((left, right) => left.name.localeCompare(right.name));
}

/**
 * Whether new work could go to this machine right now.
 *
 * Only a machine that is connected, ready, and known to have a place free is
 * eligible. A machine whose places or readings are unknown is not called
 * eligible: an unknown denominator is not room.
 */
export function machineAcceptsWork(machine: EngineRoomMachine): boolean {
  return machine.connection === 'connected'
    && machine.readiness === 'ready'
    && machine.placesFree !== null
    && machine.placesFree > 0;
}
